import { FormEvent, useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { fetchMandantConfig, fetchOrganisationen, setMandantConfig } from "@/lib/api"
import { RequireRole } from "@/auth/AuthProvider"
import { SkeletonZeile } from "@/components/Skeleton"
import { useToast } from "@/components/Toast"
import { Button, Select } from "@/components/ui"

/**
 * Settings-Seite (nur `admin`): Mandanten-Organisation (Ninox `XD`,
 * `SYSTEM_MANDANT_ORG_ID`, E5) — zeigt den aktuellen Wert und erlaubt die
 * Auswahl per Organisation-Picker statt direkter DB-ID-Eingabe.
 */
export function SettingsPage() {
  return (
    <RequireRole role="admin">
      <SettingsContent />
    </RequireRole>
  )
}

function SettingsContent() {
  useEffect(() => {
    document.title = "Einstellungen — RMNC Stammdaten"
  }, [])

  const queryClient = useQueryClient()
  const toast = useToast()
  const [auswahl, setAuswahl] = useState("")

  const { data: mandant, isLoading } = useQuery({
    queryKey: ["settings", "mandant"],
    queryFn: () => fetchMandantConfig(),
  })

  const { data: organisationen } = useQuery({
    queryKey: ["organisationen", "mandant-auswahl"],
    queryFn: () => fetchOrganisationen({ size: 500 }),
  })

  useEffect(() => {
    if (mandant?.organisationId) setAuswahl(mandant.organisationId)
  }, [mandant])

  const aktuelleOrg = useMemo(
    () => organisationen?.data.find((o) => o.id === mandant?.organisationId) ?? null,
    [organisationen, mandant],
  )

  const speichern = useMutation({
    mutationFn: (organisationId: string) => setMandantConfig(organisationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings", "mandant"] })
      toast.show("Mandanten-Organisation gespeichert.", "success")
    },
    onError: () => toast.show("Speichern fehlgeschlagen.", "error"),
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!auswahl) return
    speichern.mutate(auswahl)
  }

  return (
    // Linksbündig/volle Breite wie die Technik-Vorlage (kein mx-auto/max-w)
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Einstellungen</h1>
        <p className="text-sm text-slate-600">
          Systemweite Mandanten-Konfiguration (Ninox `XD`) — nur für Administration.
        </p>
      </header>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="mb-3 text-lg font-semibold text-slate-900">Mandanten-Organisation</h2>
        <p className="mb-4 text-sm text-slate-600">
          Diese Organisation gilt als „eigene Firma" (Mandant) — u. a. Basis für den
          Textvorlagen-Erklärungs-Endpoint (Platzhalter-Ersetzung).
        </p>

        {isLoading ? (
          <SkeletonZeile className="h-6 w-64" />
        ) : (
          <p className="mb-4 text-sm">
            Aktuell gesetzt:{" "}
            <strong className="text-slate-900">
              {aktuelleOrg?.name ?? (mandant?.organisationId ? `Organisation ${mandant.organisationId}` : "— nicht gesetzt —")}
            </strong>
          </p>
        )}

        <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3">
          <div className="min-w-64">
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Organisation auswählen
            </label>
            <Select value={auswahl} onChange={(e) => setAuswahl(e.target.value)}>
              <option value="">— auswählen —</option>
              {organisationen?.data.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Select>
          </div>
          <Button type="submit" disabled={speichern.isPending || !auswahl}>
            {speichern.isPending ? "Speichert…" : "Speichern"}
          </Button>
        </form>
      </section>
    </div>
  )
}
